// T2T-05-C3-UI — Composer: textarea, @mention picker, file attach, send.

import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import MentionPicker from "./MentionPicker";

const MAX_FILES = 3;
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const ALLOWED_MIME = [
  "application/pdf",
  "image/jpeg", "image/png", "image/webp",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

export default function MessageInputBar({ disabled, onSend, queryMentions, onKeystroke }) {
  const [text,     setText]     = useState("");
  const [files,    setFiles]    = useState([]);
  const [mentions, setMentions] = useState([]);   // [{ user_id, user_name }]
  const [pickerQ,  setPickerQ]  = useState(null); // null = closed; "" or "abc" = open
  const [pickerRows, setPickerRows] = useState([]);
  const taRef = useRef(null);
  const fileRef = useRef(null);

  // Mention picker — refresh rows when query changes
  useEffect(() => {
    if (pickerQ === null) return;
    let cancelled = false;
    queryMentions(pickerQ).then((rows) => {
      if (!cancelled) setPickerRows(rows || []);
    });
    return () => { cancelled = true; };
  }, [pickerQ, queryMentions]);

  const handleChange = (e) => {
    const v = e.target.value;
    setText(v);
    // Fire typing keystroke ping (parent debounces to 1/sec)
    if (v && onKeystroke) onKeystroke();
    // Detect @mention: open picker when an @ follows whitespace/start, capture
    // text up to caret as the query.
    const caret = e.target.selectionStart;
    const before = v.slice(0, caret);
    const m = before.match(/(?:^|\s)@([\w.-]*)$/);
    if (m) setPickerQ(m[1] || "");
    else setPickerQ(null);
  };

  const insertMention = (user) => {
    if (!user?.user_id || !user.user_name) { setPickerQ(null); return; }
    const ta = taRef.current;
    const caret = ta ? ta.selectionStart : text.length;
    const before = text.slice(0, caret).replace(/(^|\s)@([\w.-]*)$/, `$1@${user.user_name} `);
    const after  = text.slice(caret);
    setText(before + after);
    setMentions((prev) => prev.find(m => m.user_id === user.user_id) ? prev : [...prev, user]);
    setPickerQ(null);
    // Restore focus + caret
    requestAnimationFrame(() => {
      if (ta) {
        ta.focus();
        const pos = before.length;
        ta.setSelectionRange(pos, pos);
      }
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && pickerQ === null) {
      e.preventDefault();
      submit();
    }
    if (e.key === "Escape") setPickerQ(null);
  };

  const onPickFiles = (e) => {
    const incoming = Array.from(e.target.files || []);
    e.target.value = ""; // allow re-selecting the same file
    const next = [...files];
    for (const f of incoming) {
      if (next.length >= MAX_FILES) break;
      if (f.size > MAX_FILE_BYTES) {
        window.alert(`"${f.name}" exceeds the 10 MB limit.`);
        continue;
      }
      if (!ALLOWED_MIME.includes(f.type)) {
        window.alert(`"${f.name}" — file type not allowed.`);
        continue;
      }
      next.push(f);
    }
    setFiles(next);
  };

  const removeFile = (idx) => setFiles(files.filter((_, i) => i !== idx));

  const submit = async () => {
    if (disabled) return;
    const trimmed = text.trim();
    if (!trimmed && files.length === 0) return;
    // Mentioned ids only for users still referenced in the text
    const mentioned_user_ids = mentions
      .filter(m => trimmed.includes(`@${m.user_name}`))
      .map(m => m.user_id);
    await onSend({ text: trimmed, mentioned_user_ids, files });
    setText("");
    setFiles([]);
    setMentions([]);
    setPickerQ(null);
  };

  return (
    <div style={{ borderTop: "1px solid var(--t2t-border)", paddingTop: 8 }}>
      {files.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 6 }}>
          {files.map((f, i) => (
            <span
              key={i}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                background: "var(--t2t-bg)", border: "1px solid var(--t2t-border)",
                borderRadius: "var(--t2t-radius)", padding: "3px 8px", fontSize: 11,
              }}
            >
              <i className="bi bi-paperclip" />
              <span style={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {f.name}
              </span>
              <button
                type="button"
                onClick={() => removeFile(i)}
                className="t2t-btn t2t-btn-ghost t2t-btn-sm"
                style={{ padding: "0 2px", color: "var(--t2t-danger)" }}
                aria-label={`Remove ${f.name}`}
              >
                <i className="bi bi-x" />
              </button>
            </span>
          ))}
        </div>
      )}

      <div style={{ position: "relative" }}>
        {pickerQ !== null && pickerRows.length > 0 && (
          <MentionPicker
            rows={pickerRows}
            onPick={insertMention}
            onDismiss={() => setPickerQ(null)}
          />
        )}

        <div
          style={{
            display: "flex", alignItems: "flex-end", gap: 8,
            border: "1px solid var(--t2t-border)", borderRadius: "var(--t2t-radius)",
            padding: 6, background: "var(--t2t-surface)",
          }}
        >
          <button
            type="button"
            className="t2t-btn t2t-btn-ghost t2t-btn-sm"
            onClick={() => fileRef.current?.click()}
            disabled={disabled || files.length >= MAX_FILES}
            title="Attach file"
            style={{ padding: "4px 6px" }}
          >
            <i className="bi bi-paperclip" style={{ fontSize: 14 }} />
          </button>
          <input
            ref={fileRef}
            type="file"
            multiple
            accept={ALLOWED_MIME.join(",")}
            onChange={onPickFiles}
            style={{ display: "none" }}
          />

          <textarea
            ref={taRef}
            value={text}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder="Type a message…  (use @ to mention)"
            rows={1}
            disabled={disabled}
            maxLength={5000}
            style={{
              flex: 1, border: "none", outline: "none", resize: "none",
              fontSize: 13, color: "var(--t2t-text-primary)",
              background: "transparent",
              minHeight: 28, maxHeight: 120,
              fontFamily: "inherit",
            }}
          />

          <button
            type="button"
            className="t2t-btn t2t-btn-primary t2t-btn-sm"
            onClick={submit}
            disabled={disabled || (!text.trim() && files.length === 0)}
            style={{ padding: "4px 10px" }}
          >
            {disabled ? "Sending…" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}

MessageInputBar.propTypes = {
  disabled:      PropTypes.bool,
  onSend:        PropTypes.func.isRequired,
  queryMentions: PropTypes.func.isRequired,
  onKeystroke:   PropTypes.func,
};
